import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { cn } from '@tailoring-crm/ui';
import { useSession } from '../../lib/session/session-context.js';
import {
  activeGroupFor,
  groupsFor,
  hasSectionColumn,
  isSidebarItemActive,
  itemsInGroup,
} from './nav-items.js';
import { SidebarAccount } from './sidebar-account.js';

/**
 * Desktop navigation, in two columns.
 *
 * The RAIL is the sections: an icon over a label, the open one a solid tile. Beside it, the
 * COLUMN lists the screens in that section. Both hang below the top bar. Hidden below `md`,
 * where {@link BottomNav} takes over — the two are never on screen together, so a screen
 * reader meets exactly one primary navigation at any width.
 *
 * The rail is the primary landmark. The column is a second, named landmark, so "Orders" the
 * section and "Orders" the screen never collide in one list of links.
 *
 * The column can be folded away from a button at the foot of the rail, handing its width to
 * the screen — a customer's dossier read across the whole window. The rail stays, so every
 * section is still one click off. The choice is remembered (see {@link useNavCollapsed}) and
 * the button never moves, so the way back is always where the way out was.
 *
 * Across the foot of both columns sits {@link SidebarAccount}: the signed-in name, the shop's plan
 * and the app's version, folding to the rail's width with the column.
 */
export function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const role = useSession()?.role;
  const { pathname } = useLocation();
  const groups = groupsFor(role);
  const active = activeGroupFor(role, pathname);
  const items = itemsInGroup(role, active);
  const activeGroup = groups.find((group) => group.key === active);
  const hasColumn = hasSectionColumn(role, pathname);

  return (
    <aside
      className={cn(
        'fixed bottom-0 left-0 top-header z-30 hidden flex-col overflow-hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground motion-safe:duration-200 motion-safe:transition-[width] md:flex',
        collapsed ? 'w-rail' : 'w-sidebar',
      )}
    >
      <div className="flex min-h-0 flex-1">
        <nav
          aria-label={t('nav.primaryLabel')}
          className="flex w-rail shrink-0 flex-col items-stretch gap-1 overflow-y-auto border-r border-sidebar-border p-2"
        >
          {groups.map((group) => {
            const first = itemsInGroup(role, group.key)[0];
            if (!first) return null;
            const on = group.key === active;
            return (
              <NavLink
                key={group.key}
                to={first.to}
                aria-current={on ? 'page' : undefined}
                // The first-order tour starts by pointing here (see components/onboarding/tour).
                data-tour={first.to === '/customers' ? 'nav-customers' : undefined}
                className={cn(
                  'flex flex-col items-center gap-1 rounded-lg px-1 py-2 text-center text-[0.6875rem] font-medium leading-tight transition-colors',
                  on
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground hover:bg-sidebar-panel hover:text-sidebar-accent-foreground',
                )}
              >
                <group.icon aria-hidden="true" className="size-5 shrink-0" />
                <span className="max-w-full truncate">{t(group.labelKey)}</span>
              </NavLink>
            );
          })}

          {/* Nothing to fold in a one-screen section, so no button to fold it with. */}
          {hasColumn ? (
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={!collapsed}
              aria-controls="sidebar-section-column"
              aria-label={t(collapsed ? 'nav.expandSection' : 'nav.collapseSection')}
              title={t(collapsed ? 'nav.expandSection' : 'nav.collapseSection')}
              className="mt-auto flex items-center justify-center rounded-lg px-1 py-2 text-sidebar-foreground transition-colors hover:bg-sidebar-panel hover:text-sidebar-accent-foreground"
            >
              {collapsed ? (
                <PanelLeftOpen aria-hidden="true" className="size-5" />
              ) : (
                <PanelLeftClose aria-hidden="true" className="size-5" />
              )}
            </button>
          ) : null}
        </nav>

        <nav
          aria-label={t('nav.sectionLabel', {
            section: activeGroup ? t(activeGroup.labelKey) : '',
          })}
          id="sidebar-section-column"
          // A fixed width, so the links are clipped by the closing sidebar rather than reflowed
          // by it. Once folded the column is `invisible` — out of the tab order and the
          // accessibility tree — but only after the slide: visibility transitions at its end.
          className={cn(
            'flex w-[calc(theme(spacing.sidebar)-theme(spacing.rail))] shrink-0 flex-col gap-0.5 overflow-y-auto bg-sidebar-panel p-3 motion-safe:duration-200 motion-safe:transition-[visibility]',
            collapsed && 'invisible',
          )}
        >
          {activeGroup ? (
            <p className="px-2 pb-2 pt-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-sidebar-foreground/70">
              {t(activeGroup.labelKey)}
            </p>
          ) : null}
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              className={cn(
                'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                isSidebarItemActive(items, item, pathname)
                  ? 'bg-sidebar-accent/20 text-sidebar-accent-foreground'
                  : 'text-sidebar-foreground hover:bg-sidebar/60 hover:text-sidebar-accent-foreground',
              )}
            >
              <item.icon aria-hidden="true" className="size-4 shrink-0" />
              <span className="truncate">{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Who, on which plan, on which build — across the foot of whichever width is open. */}
      <SidebarAccount compact={collapsed} />
    </aside>
  );
}
